/**
 * Before/After Moderation Queue (Admin) — TCPN PHASE 10-B2B B2D
 * =============================================================
 * Minimal moderation surface. NO scoring, NO automation, NO analytics.
 *
 * Backend:
 *   GET  /api/admin/before-after-cases?state=pending_review
 *   POST /api/admin/before-after-cases/{id}/approve
 *   POST /api/admin/before-after-cases/{id}/reject
 *   POST /api/admin/before-after-cases/{id}/archive
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft, ShieldCheck, ShieldX, Archive, Loader2, CheckCircle2,
  AlertTriangle, FileText, Mail, Clock, X
} from 'lucide-react';
import api from '../../utils/api';
import AuthMediaThumb from '../../components/AuthMediaThumb';

const REJECTION_REASONS = [
  { value: 'consent_invalid', label: 'Consent invalid / missing' },
  { value: 'identifying_info_visible', label: 'Identifying info visible' },
  { value: 'prohibited_text', label: 'Prohibited or comparative text' },
  { value: 'quality_issues', label: 'Image quality issues' },
  { value: 'other', label: 'Other (explain in note)' },
];

const STATE_BADGE = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-700' },
  pending_review: { label: 'Pending review', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800' },
  archived: { label: 'Archived', cls: 'bg-slate-200 text-slate-600' },
};

// BA-002C: the local AuthMediaThumb (which carried a known blob lifecycle
// race surfacing as the "Script error." runtime overlay after approve →
// refetch) is replaced by the shared, stability-hardened component in
// `frontend/src/components/AuthMediaThumb.jsx`. That component:
//   * uses a ref for the active blob URL (no stale closure),
//   * defers `URL.revokeObjectURL` via setTimeout(0) so an in-flight
//     `<img>` render can finish before the URL becomes invalid,
//   * carries an `onError` handler on the `<img>` that swallows load
//     failures locally instead of propagating to `window.onerror`,
//   * walks a `thumbnail → preview → original` fallback chain.
//
// No other behaviour changes; admin queue cards still receive a thumb
// rendered through the authenticated blob fetch path.

const BeforeAfterModerationQueue = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [stateFilter, setStateFilter] = useState('pending_review');

  // Reject dialog
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('quality_issues');
  const [rejectNote, setRejectNote] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = stateFilter === 'all' ? {} : { state: stateFilter };
      const res = await api.get('/admin/before-after-cases', { params });
      setCases(res.data?.cases || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [stateFilter]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const approve = async (caseId) => {
    setBusyId(caseId);
    try {
      const res = await api.post(`/admin/before-after-cases/${caseId}/approve`);
      if (!res.data?.success) throw new Error(res.data?.message || 'Approve failed');
      toast.success('Case approved · public');
      fetchCases();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    setBusyId(rejectFor.case_id);
    try {
      const res = await api.post(`/admin/before-after-cases/${rejectFor.case_id}/reject`, {
        reason: rejectReason,
        note: rejectNote || null,
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Reject failed');
      toast.success('Case rejected');
      setRejectFor(null);
      setRejectNote('');
      setRejectReason('quality_issues');
      fetchCases();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  // Open admin-protected media in a new tab via an authenticated blob fetch.
  // Admin tokens are required for non-public docs (e.g. attestation PDFs);
  // a plain <a target="_blank"> link cannot send the Authorization header
  // and therefore returns 401.
  //
  // MEDIA-001 (2026-02-28): switched from `api.get(..., {responseType:'blob'})`
  // to native `fetch()`. CRA react-error-overlay accesses `xhr.responseText`
  // on non-2xx blob responses, which throws a DOMException and surfaces as
  // the cross-origin "Script error." runtime overlay. Native fetch does not
  // touch the sniffer. Same auth path, same visibility, same cleanup model.
  const openProtectedMedia = async (mediaId, variant = 'preview') => {
    if (!mediaId) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token =
      (typeof window !== 'undefined' &&
        (window.localStorage?.getItem('treatx_unified_token') ||
          window.localStorage?.getItem('treatx_patient_token'))) ||
      '';
    const url = `${BACKEND_URL}/api/media/${encodeURIComponent(mediaId)}?variant=${encodeURIComponent(variant)}`;
    let blobUrl = null;
    try {
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        headers: token ? {} : undefined,
      });
      if (!resp.ok) {
        if (resp.status === 401 || resp.status === 403) {
          toast.error('Not authorised to preview this document');
        } else {
          toast.error(`Failed to open document (HTTP ${resp.status})`);
        }
        return;
      }
      const blob = await resp.blob();
      blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!w) toast.error('Browser blocked the preview popup');
      // Best-effort cleanup so we don't leak object URLs forever.
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
      }, 60_000);
    } catch (err) {
      // Local failure — keep the message out of window.onerror.
      // eslint-disable-next-line no-console
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[openProtectedMedia] suppressed fetch error', err);
      }
      toast.error('Failed to open document');
    }
  };

  const archive = async (caseId) => {
    if (!window.confirm('Archive this case? It will be hidden from public surfaces.')) return;
    setBusyId(caseId);
    try {
      await api.post(`/admin/before-after-cases/${caseId}/archive`);
      toast.success('Case archived');
      fetchCases();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Archive failed');
    } finally {
      setBusyId(null);
    }
  };

  // ── TREATX-BA-KILL-SWITCH-ADMIN-UI-01 ────────────────────────────
  // Visibility controls: global env state (read-only) + per-clinic toggle.
  const [globalVis, setGlobalVis] = useState(null);
  const [globalBusy, setGlobalBusy] = useState(false);
  const [visClinicId, setVisClinicId] = useState('');
  const [visState, setVisState] = useState(null);
  const [visReason, setVisReason] = useState('');
  const [visBusy, setVisBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/ba/global-visibility');
        setGlobalVis(res.data);
      } catch {
        setGlobalVis({ enabled: null, note: 'Unable to load global state.' });
      }
    })();
  }, []);

  const loadClinicVisibility = async () => {
    if (!visClinicId.trim()) {
      toast.error('Enter a clinic id');
      return;
    }
    setVisBusy(true);
    try {
      const res = await api.get(`/admin/clinics/${visClinicId.trim()}/ba-visibility`);
      setVisState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Lookup failed');
      setVisState(null);
    } finally {
      setVisBusy(false);
    }
  };

  const toggleClinicVisibility = async (next) => {
    if (!visState?.clinic_id) return;
    if (next && !visReason.trim()) {
      toast.error('Enter a reason before disabling');
      return;
    }
    setVisBusy(true);
    try {
      const res = await api.put(
        `/admin/clinics/${visState.clinic_id}/ba-visibility`,
        { ba_disabled: next, reason: visReason.trim() || null },
      );
      setVisState(res.data);
      setVisReason('');
      toast.success(next ? 'BA visibility disabled for clinic' : 'BA visibility restored');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed');
    } finally {
      setVisBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin')} className="mb-2 -ml-3" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4 mr-1" /> Admin
        </Button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-blue-600" />
              Before/After Moderation
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Review cases for consent validity, identifying info, and policy compliance.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {['pending_review', 'approved', 'rejected', 'archived', 'all'].map((s) => (
            <Button
              key={s}
              variant={stateFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStateFilter(s)}
              data-testid={`mod-filter-${s}`}
            >
              {s === 'all' ? 'All' : (STATE_BADGE[s]?.label || s)}
            </Button>
          ))}
        </div>

        {/* TREATX-BA-KILL-SWITCH-ADMIN-UI-01 — Visibility controls */}
        <Card className="mb-6" data-testid="ba-visibility-controls">
          <CardContent className="py-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">BA Visibility Controls</h3>
              <p className="text-xs text-slate-500 mt-1">
                Toggle public Before/After visibility per clinic. Disables the public
                gallery, search badge, and direct media endpoint without deleting any files.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-3"
                 data-testid="ba-vis-global-state">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="font-medium text-slate-700">Global BA visibility:&nbsp;</span>
                  <span className={globalVis?.enabled ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}
                        data-testid="ba-vis-global-status">
                    {globalVis === null ? '…' : globalVis.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <Button
                  variant={globalVis?.enabled ? 'destructive' : 'default'}
                  size="sm"
                  disabled={globalBusy || globalVis === null || globalVis?.env_enabled === false}
                  onClick={async () => {
                    const turningOff = !!globalVis?.enabled;
                    const verb = turningOff ? 'DISABLE' : 'ENABLE';
                    const msg = turningOff
                      ? 'This will HIDE all public Before/After media (gallery, search badges, direct media). Continue?'
                      : 'This will allow public Before/After media to render again (subject to per-clinic + per-case rules). Continue?';
                    if (!window.confirm(`${verb} global Before/After public visibility?\n\n${msg}`)) return;
                    const reason = window.prompt(`Optional reason for audit log (${verb}):`, '');
                    setGlobalBusy(true);
                    try {
                      const res = await api.put('/admin/ba/global-visibility', {
                        enabled: !turningOff,
                        reason: reason || null,
                      });
                      setGlobalVis({
                        enabled: res?.data?.effective_enabled,
                        env_enabled: res?.data?.env_enabled,
                        admin_enabled: res?.data?.admin_enabled,
                        note: 'Updated. Effective = env AND admin-runtime.',
                        updated_at: res?.data?.updated_at,
                        updated_by: res?.data?.updated_by,
                        reason: res?.data?.reason,
                      });
                    } catch (err) {
                      window.alert('Could not update: ' + (err?.response?.data?.detail || err.message));
                    } finally {
                      setGlobalBusy(false);
                    }
                  }}
                  data-testid="ba-vis-global-toggle-btn"
                >
                  {globalBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : globalVis?.enabled ? 'Turn OFF (kill switch)' : 'Turn ON'}
                </Button>
              </div>
              <div className="text-slate-500 text-[11px] space-y-0.5">
                {globalVis?.env_enabled === false && (
                  <p className="text-red-700">
                    Env hard-lock active (<code>BA_PUBLIC_VISIBILITY_ENABLED=false</code>). UI toggle is disabled until env is flipped on by a deployer.
                  </p>
                )}
                {globalVis?.reason && <p>Last reason: {globalVis.reason}</p>}
                {globalVis?.updated_by && (
                  <p>
                    Last changed by <span className="font-medium">{globalVis.updated_by}</span>
                    {globalVis.updated_at && ` at ${globalVis.updated_at}`}
                  </p>
                )}
                <p>
                  Effective = env AND admin-runtime. Per-clinic visibility cannot override an OFF global.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={visClinicId}
                onChange={(e) => setVisClinicId(e.target.value)}
                placeholder="Clinic ID"
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="ba-vis-clinic-input"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={visBusy}
                onClick={loadClinicVisibility}
                data-testid="ba-vis-load-btn"
              >
                {visBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Look up'}
              </Button>
            </div>

            {visState && (
              <div className="border border-slate-200 rounded-md p-3 space-y-2 text-xs"
                   data-testid="ba-vis-clinic-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{visState.clinic_name}</p>
                    <p className="text-slate-500">{visState.clinic_id}</p>
                  </div>
                  <span
                    className={
                      visState.ba_disabled
                        ? 'px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium'
                        : 'px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium'
                    }
                    data-testid="ba-vis-clinic-status"
                  >
                    {visState.ba_disabled ? 'Disabled' : 'Enabled'}
                  </span>
                </div>
                {visState.ba_disabled && (
                  <div className="text-slate-600 space-y-1">
                    {visState.ba_disabled_at && <p>Disabled at: {visState.ba_disabled_at}</p>}
                    {visState.ba_disabled_by && <p>By: {visState.ba_disabled_by}</p>}
                    {visState.ba_disabled_reason && <p>Reason: {visState.ba_disabled_reason}</p>}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <input
                    type="text"
                    value={visReason}
                    onChange={(e) => setVisReason(e.target.value)}
                    placeholder={visState.ba_disabled ? 'Reason for re-enable (optional)' : 'Reason for disable (required)'}
                    className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                    data-testid="ba-vis-reason-input"
                  />
                  {visState.ba_disabled ? (
                    <Button
                      size="sm"
                      onClick={() => toggleClinicVisibility(false)}
                      disabled={visBusy}
                      data-testid="ba-vis-enable-btn"
                    >
                      Re-enable
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => toggleClinicVisibility(true)}
                      disabled={visBusy}
                      data-testid="ba-vis-disable-btn"
                    >
                      Disable
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : cases.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-500" data-testid="mod-empty">
              <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p>Queue is empty.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="mod-list">
            {cases.map((c) => {
              const badge = STATE_BADGE[c.state] || { label: c.state, cls: 'bg-slate-100 text-slate-700' };
              const consent = c.consent_summary || {};
              return (
                <Card key={c.case_id} data-testid={`mod-case-${c.case_id}`}>
                  <CardContent className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-4">
                      {/* Media pair */}
                      <div className="grid grid-cols-2 gap-1">
                        <div className="aspect-square bg-slate-100 rounded overflow-hidden">
                          <AuthMediaThumb mediaId={c.before_media_id} alt="before" />
                        </div>
                        <div className="aspect-square bg-slate-100 rounded overflow-hidden">
                          <AuthMediaThumb mediaId={c.after_media_id} alt="after" />
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={badge.cls}>{badge.label}</Badge>
                          <span className="text-sm font-medium text-slate-900 truncate">{c.clinic_name}</span>
                          <span className="text-xs text-slate-400 font-mono">· {c.treatment_slug}</span>
                        </div>
                        {c.caption && <p className="text-sm text-slate-700">{c.caption}</p>}
                        <div className="text-xs text-slate-500 space-y-1 pt-1">
                          <div className="flex items-center gap-2">
                            {consent.withdrawn ? (
                              <Badge className="bg-rose-100 text-rose-700 border-0">
                                <AlertTriangle className="h-3 w-3 mr-1" /> consent withdrawn
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 border-0">
                                <ShieldCheck className="h-3 w-3 mr-1" /> consent valid
                              </Badge>
                            )}
                            <span className="text-slate-500">path: <strong>{consent.consent_path}</strong></span>
                            {consent.user_email && (
                              <span className="text-slate-500 flex items-center gap-1">
                                <Mail className="h-3 w-3" /> {consent.user_email}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            <span>consented {consent.consented_at?.slice(0, 19)?.replace('T', ' ')}</span>
                            {consent.attestation_doc_media_id && (
                              <button
                                type="button"
                                onClick={() => openProtectedMedia(consent.attestation_doc_media_id, 'preview')}
                                className="text-blue-600 hover:underline flex items-center gap-1"
                                data-testid={`view-attestation-${c.case_id}`}
                              >
                                <FileText className="h-3 w-3" /> attestation doc
                              </button>
                            )}
                          </div>
                          {c.rejection_reason && (
                            <div className="text-rose-600">
                              Rejected: <strong>{c.rejection_reason}</strong>
                              {c.rejection_note ? ` — ${c.rejection_note}` : ''}
                            </div>
                          )}
                          {/* TREATX-L1E: regulatory dates + provider attestations
                              evidence — moderator MUST verify before approving. */}
                          {c.l1e_compliance && (
                            <div
                              className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 space-y-1"
                              data-testid={`l1e-compliance-${c.case_id}`}
                            >
                              <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
                                L1-E compliance
                              </div>
                              <div className="grid grid-cols-2 gap-x-3 text-[11px]">
                                <div><span className="text-slate-500">procedure:</span> {c.l1e_compliance.procedure_date || '—'}</div>
                                <div><span className="text-slate-500">first published:</span> {c.l1e_compliance.first_publish_date || '—'}</div>
                                <div><span className="text-slate-500">before captured:</span> {c.l1e_compliance.before_captured_at || '—'}</div>
                                <div><span className="text-slate-500">after captured:</span> {c.l1e_compliance.after_captured_at || '—'}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-3 text-[11px] pt-1">
                                {[
                                  ['same_environment_confirmed', 'same environment'],
                                  ['no_retouch_confirmed', 'no retouch'],
                                  ['no_privacy_violation_confirmed', 'no privacy violation'],
                                  ['no_incentive_offered_confirmed', 'no incentive'],
                                ].map(([k, lbl]) => (
                                  <div key={k} className="flex items-center gap-1">
                                    {c.l1e_compliance[k]
                                      ? <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                      : <AlertTriangle className="h-3 w-3 text-amber-600" />}
                                    <span className={c.l1e_compliance[k] ? 'text-emerald-800' : 'text-amber-800'}>{lbl}</span>
                                  </div>
                                ))}
                              </div>
                              {consent.media_binding && (
                                <div className="text-[10px] text-slate-500 pt-1">
                                  consent bound to media:{' '}
                                  <span className={
                                    consent.media_binding.before_media_id === c.before_media_id
                                    && consent.media_binding.after_media_id === c.after_media_id
                                      ? 'text-emerald-700 font-medium' : 'text-rose-700 font-medium'
                                  } data-testid={`l1e-consent-media-match-${c.case_id}`}>
                                    {consent.media_binding.before_media_id === c.before_media_id
                                     && consent.media_binding.after_media_id === c.after_media_id
                                      ? 'match ✓' : 'MISMATCH — do NOT approve'}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex md:flex-col gap-2 md:items-end">
                        {c.state === 'pending_review' && !consent.withdrawn && (
                          <Button
                            size="sm"
                            onClick={() => approve(c.case_id)}
                            disabled={busyId === c.case_id}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-${c.case_id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                          </Button>
                        )}
                        {(c.state === 'pending_review' || c.state === 'approved') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setRejectFor(c); setRejectReason('quality_issues'); setRejectNote(''); }}
                            disabled={busyId === c.case_id}
                            className="border-rose-300 text-rose-700 hover:bg-rose-50"
                            data-testid={`reject-${c.case_id}`}
                          >
                            <ShieldX className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        )}
                        {c.state !== 'archived' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => archive(c.case_id)}
                            disabled={busyId === c.case_id}
                            data-testid={`mod-archive-${c.case_id}`}
                          >
                            <Archive className="h-4 w-4 mr-1" /> Archive
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ─────────────── Reject Dialog ─────────────── */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reject-modal">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Reject case</h2>
                <Button variant="ghost" size="sm" onClick={() => setRejectFor(null)} data-testid="close-reject">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-sm text-slate-600">
                Case <strong className="font-mono">{rejectFor.case_id}</strong> — {rejectFor.clinic_name}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reason</label>
                <select
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2"
                  data-testid="reject-reason"
                >
                  {REJECTION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Note (optional, internal)</label>
                <Textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. patient face visible at frame 0:02"
                  data-testid="reject-note"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRejectFor(null)} data-testid="cancel-reject">Cancel</Button>
                <Button
                  onClick={submitReject}
                  disabled={busyId === rejectFor.case_id}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="confirm-reject"
                >
                  Confirm rejection
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BeforeAfterModerationQueue;
